class VariantSelects extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {};

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }
  }

  // Run methods when the element connects to the DOM
  connectedCallback() {
    // Listen to variant changes
    this.addEventListener('change', this.onVariantChange);

    // Get the current variant data and set as current variant
    this.getSelectedVariantOptions();
  }

  onVariantChange() {
    // Get the selected variant options
    this.getSelectedVariantOptions();

    // Get the current variant data and set as current variant
    this.currentVariant = this.getCurrentVariantData();

    if (this.currentVariant) {
      // Update the input with the new variant id
      this.updateVariantInput();

      // Dispatch the variant change event
      this.dispatchVariantChange();
    }
  }

  // Get the current selected variant options from the selects
  getSelectedVariantOptions() {
    this.variantOptions = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  // Update the current variant based on the selected options
  getCurrentVariantData() {
    return this.getProductVariantsData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          if (!this.variantOptions[index]) {
            return true;
          }
          return this.variantOptions[index] === option;
        })
        .includes(false);
    });
  }

  // Dispatch the variant change event
  dispatchVariantChange() {
    this.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        detail: {
          variant: this.currentVariant,
          product: this.options.productId,
          section: this.options.sectionId,
        },
      })
    );
  }

  // Update the input with the new variant id
  updateVariantInput() {
    this.idInput = this.idInput || this.querySelector('input[name="id"]');
    this.idInput.value = this.currentVariant.id;
  }

  // Get the variant data from the JSON script tag
  getProductVariantsData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

/**
 * Variant Radio inputs
 */
class VariantRadios extends VariantSelects {
  constructor() {
    super();

    this.getSelectedVariantOptions();

    // Get the current variant data and set as current variant
    this.currentVariant = this.getCurrentVariantData();

    // Highlight available options
    this.highlightOptions();
  }

  onVariantChange() {
    super.onVariantChange();
    this.highlightOptions();
  }

  // Get the current selected variant options from the radio's
  getSelectedVariantOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.variantOptions = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }

  // Check for each option value if the other one/two selected variant options are available
  highlightOptions() {
    let selectedElement = this.querySelector('[data-option-group] input[checked]');

    // If there are no selected elements make all available
    if (!selectedElement) {
      this.querySelectorAll('[data-option-group]').forEach((option) => {
        option.querySelectorAll('[data-option]').forEach((optionEl) => {
          let label = optionEl.querySelector('[data-option-label]');
          label.classList.remove('option__label--disabled');
        });
      });
      return;
    }

    // Loop over option groups
    this.querySelectorAll('[data-option-group]').forEach((option) => {
      // Get index from option group (1, 2 or 3)
      const optionIndex = parseInt(option.dataset.optionGroup);

      // Create compare options from group, remove selected option based on index
      const compareOptions = [...this.variantOptions].filter(
        (option, index) => index != optionIndex
      );

      // Loop over possible values for option group
      option.querySelectorAll('[data-option]').forEach((optionEl) => {
        let label = optionEl.querySelector('[data-option-label]'),
          input = optionEl.querySelector('[data-option-input]'),
          value = input.value;

        // Match option availability and check if the two current options are available for value
        let match = this.getProductVariantsData().filter((variant) => {
          // Get available variants where the current option index is the option value
          if (variant.options[optionIndex] == value && variant.available) {
            // Create variant options, remove option based on option value
            const variantOptions = [...variant.options].filter(
              (option, index) => index != optionIndex
            );

            // Check if compare options is the same as variant options
            return variantOptions.every(
              (item, index) => item.toLowerCase() === compareOptions[index].toLowerCase()
            );
          }

          return false;
        });

        // Debug current index, current value, compare options and match
        // console.log(optionIndex, value, compareOptions, match);

        // Enable disabled variant options
        label.classList.toggle('option__label--disabled', match.length == 0);
      });
    });
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}
